/**
 * A word between tabs before a new one gives up on the cookie.
 *
 * A tab that boots with no mark of its own on a device nobody asked to be
 * remembered on would clear the cookie and show the sign-in page. Right for
 * the next person at a counter tablet; wrong for a teller who has just
 * opened a link in a new tab, since clearing the cookie also ends the tab
 * they came from. So before it does, the new tab asks over a channel the
 * other tabs of this origin can hear: is anybody signed in? A tab that holds
 * a session says so, and the new tab takes the session up as its own, mark
 * and all. Only when nobody answers inside a short wait does it clear.
 *
 * The wait is short because a tab that is there answers in a millisecond
 * or two, and the only tabs that are not are the ones that do not exist. A
 * browser without the channel is told nobody answered, which is the
 * behaviour before there was a question.
 *
 * The deciding is kept apart from the channel, so it can be run against a
 * clock in a test: given a way to listen and a wait, it settles on the first
 * answer or the timer, whichever is first.
 */

export const HANDSHAKE_CHANNEL = "hrsys.auth.tabs";

/** How long a new tab waits for an answer before concluding it is alone. */
export const HANDSHAKE_WAIT_MS = 300;

const ASK = "anyone-signed-in";
const ALIVE = "signed-in-here";

interface Word {
  type: string;
}

function isWord(data: unknown, type: string): boolean {
  return typeof data === "object" && data !== null && (data as Word).type === type;
}

/** The question a new tab puts. */
export function isAsk(data: unknown): boolean {
  return isWord(data, ASK);
}

/** The answer a signed-in tab gives. */
export function isAlive(data: unknown): boolean {
  return isWord(data, ALIVE);
}

/** A way to hear what the other tabs say; returns the way to stop. */
export type Listen = (onMessage: (data: unknown) => void) => () => void;

/**
 * Settles on whether another tab is signed in: true on the first answer that
 * says so, false when the wait runs out first. Stops listening either way,
 * and anything heard after that is nobody's business.
 */
export function waitForAnswer(listen: Listen, waitMs: number = HANDSHAKE_WAIT_MS): Promise<boolean> {
  let stop: () => void = () => undefined;
  const answer = new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => resolve(false), waitMs);
    stop = listen((data) => {
      if (!isAlive(data)) return;
      clearTimeout(timer);
      resolve(true);
    });
  });
  // A promise settles once, so a second answer or a late one changes
  // nothing; and however it settled, the listening stops.
  return answer.finally(() => stop());
}

/**
 * Asks the other tabs of this origin whether one of them is signed in.
 *
 * Resolves false where the browser has no channel: the tab behaves as it
 * did before, and clears.
 */
export async function askOtherTabs(waitMs: number = HANDSHAKE_WAIT_MS): Promise<boolean> {
  if (typeof BroadcastChannel === "undefined") return false;
  const channel = new BroadcastChannel(HANDSHAKE_CHANNEL);
  try {
    const answer = waitForAnswer((onMessage) => {
      const hear = (event: MessageEvent) => onMessage(event.data);
      channel.addEventListener("message", hear);
      return () => channel.removeEventListener("message", hear);
    }, waitMs);
    channel.postMessage({ type: ASK });
    return await answer;
  } catch {
    return false;
  } finally {
    channel.close();
  }
}

/**
 * Answers, for as long as the caller lets it, that this tab is signed in.
 * Returns the way to stop answering; a tab that signs out stops at once, so
 * it does not vouch for a session it no longer has.
 */
export function answerOtherTabs(): () => void {
  if (typeof BroadcastChannel === "undefined") return () => undefined;
  const channel = new BroadcastChannel(HANDSHAKE_CHANNEL);
  const hear = (event: MessageEvent) => {
    if (isAsk(event.data)) channel.postMessage({ type: ALIVE });
  };
  channel.addEventListener("message", hear);
  return () => {
    channel.removeEventListener("message", hear);
    channel.close();
  };
}
